import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'react-toastify';
import {
  Camera,
  CameraOff,
  CheckCircle2,
  AlertTriangle,
  Search,
  Ticket as TicketIcon,
  RotateCcw,
} from 'lucide-react';
import PageHeader from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { getError } from '../../utils';
import ticketService from '../../services/ticket.service';

const SCANNER_ELEMENT_ID = 'gotalottery-ticket-scanner';

const formatDateTime = iso => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

/**
 * Status pill — colors the result card depending on outcome.
 */
function StatusBadge({ tone, children }) {
  const map = {
    success: 'bg-green-100 text-green-800',
    warning: 'bg-amber-100 text-amber-800',
    error: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${map[tone] || map.info}`}
    >
      {children}
    </span>
  );
}

export default function ScanTicket() {
  const [scanning, setScanning] = useState(false);
  const [manualId, setManualId] = useState('');
  const [busy, setBusy] = useState(false);
  /** result = { kind: 'success' | 'already' | 'error' | 'preview' | 'choose', ticket?, tickets?, message? } */
  const [result, setResult] = useState(null);
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ id: '', at: 0 });

  // Stop scanner on unmount — inlined so we don't capture a function from outside the effect
  useEffect(() => {
    return () => {
      const instance = scannerRef.current;
      scannerRef.current = null;
      if (instance) {
        instance
          .stop()
          .catch(() => {})
          .then(() => instance.clear?.())
          .catch(() => {});
      }
    };
  }, []);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch {
        // ignore — scanner may already be stopped
      }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const handleScanned = async decodedText => {
    // Debounce: html5-qrcode fires many times per second; only act on a fresh code.
    const now = Date.now();
    if (lastScanRef.current.id === decodedText && now - lastScanRef.current.at < 3000) {
      return;
    }
    lastScanRef.current = { id: decodedText, at: now };
    await redeem(decodedText);
  };

  const startScanner = async () => {
    setResult(null);
    try {
      const instance = new Html5Qrcode(SCANNER_ELEMENT_ID);
      scannerRef.current = instance;
      await instance.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        handleScanned,
        () => {}
      );
      setScanning(true);
    } catch (err) {
      scannerRef.current = null;
      setScanning(false);
      toast.error(
        err?.message || 'No se pudo iniciar la cámara. Verificá los permisos o usá entrada manual.'
      );
    }
  };

  const redeem = async rawId => {
    const id = String(rawId || '').trim();
    if (!id || busy) return;
    setBusy(true);
    try {
      const data = await ticketService.redeemTicket(id);
      setResult({ kind: 'success', ticket: data.ticket, message: 'Ticket canjeado' });
      toast.success('Ticket canjeado');
    } catch (err) {
      const status = err?.response?.status;
      const resp = err?.response?.data;
      if (status === 409 && resp?.tickets?.length) {
        // Voucher id with several tickets — operator picks which one to redeem
        setResult({
          kind: 'choose',
          tickets: resp.tickets,
          message: resp.message || 'El comprobante tiene varios tickets. Seleccioná cuál canjear.',
        });
      } else if (status === 409 && resp?.ticket) {
        setResult({
          kind: 'already',
          ticket: resp.ticket,
          message: 'Este ticket ya fue canjeado',
        });
      } else if (status === 400 && resp?.ticket) {
        setResult({
          kind: 'error',
          ticket: resp.ticket,
          message: resp.message || 'El ticket no es válido para canjear',
        });
      } else {
        setResult({
          kind: 'error',
          ticket: null,
          message: getError(err),
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const lookup = async () => {
    const id = manualId.trim();
    if (!id || busy) return;
    setBusy(true);
    try {
      const data = await ticketService.lookupTicket(id);
      if (data?.multiple) {
        setResult({
          kind: 'choose',
          tickets: data.tickets,
          message: `El comprobante tiene ${data.tickets.length} tickets.`,
        });
      } else {
        setResult({ kind: 'preview', ticket: data });
      }
    } catch (err) {
      setResult({ kind: 'error', ticket: null, message: getError(err) });
    } finally {
      setBusy(false);
    }
  };

  const handleManualSubmit = async e => {
    e.preventDefault();
    await redeem(manualId);
  };

  const reset = () => {
    setResult(null);
    setManualId('');
    lastScanRef.current = { id: '', at: 0 };
  };

  return (
    <div className='space-y-6'>
      <PageHeader
        title='Validar ticket'
        subtitle='Escaneá el QR del ticket o ingresá su código para marcarlo como canjeado.'
      />

      <div className='grid gap-4 lg:grid-cols-2'>
        {/* ── Scanner card ── */}
        <Card>
          <CardContent className='space-y-3 p-5'>
            <div className='flex items-center justify-between'>
              <h2 className='text-sm font-semibold'>Escanear con cámara</h2>
              {scanning ? (
                <Button size='sm' variant='outline' onClick={stopScanner}>
                  <CameraOff className='h-4 w-4' />
                  Detener
                </Button>
              ) : (
                <Button size='sm' onClick={startScanner} disabled={busy}>
                  <Camera className='h-4 w-4' />
                  Iniciar cámara
                </Button>
              )}
            </div>

            <div
              id={SCANNER_ELEMENT_ID}
              className='aspect-square w-full overflow-hidden rounded-lg border border-dashed border-border bg-muted/30'
            />

            {!scanning && (
              <p className='text-xs text-muted-foreground'>
                Permití acceso a la cámara cuando el navegador lo solicite. Funciona mejor en
                móviles. Si no podés usar la cámara, ingresá el código a mano abajo.
              </p>
            )}
          </CardContent>
        </Card>

        {/* ── Manual entry card ── */}
        <Card>
          <CardContent className='space-y-3 p-5'>
            <h2 className='text-sm font-semibold'>Ingreso manual</h2>
            <form onSubmit={handleManualSubmit} className='space-y-2'>
              <Input
                value={manualId}
                onChange={e => setManualId(e.target.value)}
                placeholder='Comprobante (ej: XL-2026-05-EFD0) o ID del ticket'
                autoComplete='off'
                spellCheck={false}
              />
              <div className='flex gap-2'>
                <Button
                  type='button'
                  variant='outline'
                  onClick={lookup}
                  disabled={!manualId || busy}
                >
                  <Search className='h-4 w-4' />
                  Ver
                </Button>
                <Button type='submit' disabled={!manualId || busy} className='flex-1'>
                  <CheckCircle2 className='h-4 w-4' />
                  Canjear
                </Button>
              </div>
            </form>
            <p className='text-xs text-muted-foreground'>
              Ingresá el número de comprobante impreso en el PDF del boleto o el ID del ticket.
              &ldquo;Ver&rdquo; muestra los datos sin canjear. &ldquo;Canjear&rdquo; marca el ticket
              como utilizado.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ── Result card ── */}
      {result && (
        <Card>
          <CardContent className='space-y-4 p-5'>
            <div className='flex items-start justify-between gap-3'>
              {result.kind === 'success' && (
                <StatusBadge tone='success'>
                  <CheckCircle2 className='h-3.5 w-3.5' />
                  Canjeado
                </StatusBadge>
              )}
              {result.kind === 'already' && (
                <StatusBadge tone='warning'>
                  <AlertTriangle className='h-3.5 w-3.5' />
                  Ya canjeado
                </StatusBadge>
              )}
              {result.kind === 'preview' && (
                <StatusBadge tone='info'>
                  <TicketIcon className='h-3.5 w-3.5' />
                  Vista previa
                </StatusBadge>
              )}
              {result.kind === 'choose' && (
                <StatusBadge tone='info'>
                  <TicketIcon className='h-3.5 w-3.5' />
                  Seleccionar ticket
                </StatusBadge>
              )}
              {result.kind === 'error' && (
                <StatusBadge tone='error'>
                  <AlertTriangle className='h-3.5 w-3.5' />
                  Error
                </StatusBadge>
              )}
              <Button variant='ghost' size='sm' onClick={reset}>
                <RotateCcw className='h-4 w-4' />
                Limpiar
              </Button>
            </div>

            {result.message && (
              <p
                className={`text-sm font-medium ${
                  result.kind === 'error'
                    ? 'text-destructive'
                    : result.kind === 'already'
                      ? 'text-amber-700'
                      : 'text-foreground'
                }`}
              >
                {result.message}
              </p>
            )}

            {result.ticket && (
              <dl className='grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2'>
                <div>
                  <dt className='text-xs uppercase tracking-wide text-muted-foreground'>
                    Nº de serie
                  </dt>
                  <dd className='font-mono font-semibold'>{result.ticket.nroSerial || '—'}</dd>
                </div>
                <div>
                  <dt className='text-xs uppercase tracking-wide text-muted-foreground'>Estado</dt>
                  <dd className='font-medium'>{result.ticket.estado || '—'}</dd>
                </div>
                <div className='sm:col-span-2'>
                  <dt className='text-xs uppercase tracking-wide text-muted-foreground'>Bono</dt>
                  <dd>{result.ticket.rifa?.nombre || '—'}</dd>
                </div>
                <div>
                  <dt className='text-xs uppercase tracking-wide text-muted-foreground'>
                    Comprador
                  </dt>
                  <dd>{result.ticket.comprador?.nombre || '—'}</dd>
                </div>
                <div>
                  <dt className='text-xs uppercase tracking-wide text-muted-foreground'>Email</dt>
                  <dd className='break-all'>{result.ticket.comprador?.email || '—'}</dd>
                </div>
                {result.ticket.redeemed && (
                  <>
                    <div>
                      <dt className='text-xs uppercase tracking-wide text-muted-foreground'>
                        Canjeado el
                      </dt>
                      <dd>{formatDateTime(result.ticket.redeemedAt)}</dd>
                    </div>
                    <div>
                      <dt className='text-xs uppercase tracking-wide text-muted-foreground'>
                        Canjeado por
                      </dt>
                      <dd>
                        {result.ticket.redeemedBy?.nombre || result.ticket.redeemedBy?.email || '—'}
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            )}

            {result.kind === 'preview' && !result.ticket?.redeemed && (
              <Button onClick={() => redeem(result.ticket._id)} disabled={busy}>
                <CheckCircle2 className='h-4 w-4' />
                Canjear ahora
              </Button>
            )}

            {result.kind === 'choose' && result.tickets?.length > 0 && (
              <ul className='divide-y divide-border rounded-lg border border-border'>
                {result.tickets.map(tk => (
                  <li key={tk._id} className='flex items-center justify-between gap-3 px-4 py-3'>
                    <div className='min-w-0'>
                      <p className='font-mono text-sm font-semibold'>Nº {tk.nroSerial || '—'}</p>
                      <p className='text-xs text-muted-foreground'>
                        {tk.rifa?.nombre || ''}
                        {tk.redeemed
                          ? ` — canjeado el ${formatDateTime(tk.redeemedAt)}`
                          : ` — ${tk.estado || 'sin estado'}`}
                      </p>
                    </div>
                    {tk.redeemed ? (
                      <StatusBadge tone='warning'>
                        <AlertTriangle className='h-3.5 w-3.5' />
                        Ya canjeado
                      </StatusBadge>
                    ) : (
                      <Button size='sm' onClick={() => redeem(tk._id)} disabled={busy}>
                        <CheckCircle2 className='h-4 w-4' />
                        Canjear
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
